// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/contador/ArquivosFiscaisBotao.tsx
//  Gera o SPED Fiscal (EFD ICMS/IPI) ou o Sintegra do mês a partir dos XMLs do cofre.
//  O arquivo baixa na hora; o diálogo mostra o que ficou de fora para o contador
//  completar no programa dele (inventário, E116, CT-e, CPF do contador…).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { AlertTriangle, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { contadorService, rotuloCompetencia, type ArquivoFiscal } from './contadorService';

type Tipo = 'sped-fiscal' | 'sintegra';

const TITULO: Record<Tipo, string> = { 'sped-fiscal': 'SPED Fiscal (EFD ICMS/IPI)', sintegra: 'Sintegra' };

export function ArquivosFiscaisBotao({ contribuinteId, competencia, tipo }: { contribuinteId: number; competencia: number; tipo: Tipo }) {
  const [aberto, setAberto] = useState(false);
  const [perfil, setPerfil] = useState('A');
  const [finalidade, setFinalidade] = useState('1');
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState<ArquivoFiscal | null>(null);

  const gerar = async () => {
    setGerando(true);
    try {
      setResultado(await contadorService.arquivoFiscal(contribuinteId, tipo, competencia,
        tipo === 'sped-fiscal' ? { perfil } : { finalidade: Number(finalidade) }));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setGerando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => { setAberto(v); if (!v) setResultado(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline"><FileSpreadsheet className="mr-2 h-4 w-4" />{tipo === 'sped-fiscal' ? 'SPED Fiscal' : 'Sintegra'}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{TITULO[tipo]} de {rotuloCompetencia(competencia).toLowerCase()}</DialogTitle>
          <DialogDescription>
            Montado com as NF-e e NFC-e do cofre. Serve de base: o que os XMLs não trazem aparece na lista de avisos para completar no seu programa.
          </DialogDescription>
        </DialogHeader>

        {!resultado ? (
          <div className="space-y-1.5">
            {tipo === 'sped-fiscal' ? (
              <>
                <Label>Perfil de apresentação</Label>
                <Select value={perfil} onValueChange={setPerfil}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Perfil A</SelectItem>
                    <SelectItem value="B">Perfil B</SelectItem>
                    <SelectItem value="C">Perfil C</SelectItem>
                  </SelectContent>
                </Select>
              </>
            ) : (
              <>
                <Label>Finalidade</Label>
                <Select value={finalidade} onValueChange={setFinalidade}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Normal</SelectItem>
                    <SelectItem value="2">Retificação total</SelectItem>
                    <SelectItem value="3">Retificação aditiva</SelectItem>
                    <SelectItem value="5">Desfazimento</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">{resultado.nome}</span> baixado: {resultado.documentos} documento(s), {resultado.linhas} linha(s).
            </p>
            {resultado.avisos.length > 0 && (
              <div className="rounded-md border border-amber-500 p-3 space-y-1">
                <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 text-amber-600" />Complete antes de transmitir</p>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                  {resultado.avisos.map((a) => <li key={a}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {resultado ? (
            <Button variant="outline" onClick={() => setResultado(null)}>Gerar de novo</Button>
          ) : (
            <Button onClick={gerar} disabled={gerando}>
              {gerando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}Gerar e baixar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
